import { ScreenKey } from '@/Enums/Screens/ScreenKey.enum';
import { Role } from '@/Enums/Users/Role.enum';
import IScreenAccess from '@/Interfaces/Users/ScreenAccess.interface';
import type { ITabGroup } from '@/Interfaces/Users/TabGroup.interface';
import { ISODateString } from '@/types/DateTime.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { SnakeToCamel } from 'snake-camel-types';
const API_ENDPOINT = '/users/me';

const ALL_SCREENS = Object.values(ScreenKey);

interface IUserResponse {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string | null;
  role_id: Role;
  whitelabel_id: number;
  created_at: ISODateString | null;
  agency_id: ISODateString | null;
  advertiser_id: number | null;
  screens: IScreenAccess[];
  can_approve_campaigns: boolean;
  fpd_agreement_signed_at: string | null;
  deleted_at: string | null;
  fullName: string;
  menu_links: { label: string; link: string }[] | null;
  client_ids: null | number[];
  hide_deals_in_impressions_by_type: boolean;
  tab_groups: {
    groups: ITabGroup[];
    ungrouped_screens: ScreenKey[];
  };
}

interface IUserProfile extends SnakeToCamel<Pick<IUserResponse, 'id' | 'email' | 'first_name' | 'last_name'>> {
  phone: IUserResponse['phone_number'];
}

interface IParsedUser extends SnakeToCamel<Pick<IUserResponse, 'menu_links' | 'can_approve_campaigns'>> {
  userProfile: IUserProfile;
  role: IUserResponse['role_id'];
  whitelabel: IUserResponse['whitelabel_id'];
  screens: IUserResponse['screens'];
  fpdAgreementDate: IUserResponse['fpd_agreement_signed_at'];
  tabGroups: ITabGroup[];
  ungroupedScreens: ScreenKey[];
  hideDealsInImpressionsByType: boolean;
}

interface IState {
  loading: boolean;
  userProfile: IUserProfile;
  fpdAgreementDate: IParsedUser['fpdAgreementDate'] | null;
  screens: IParsedUser['screens'];
  role: IParsedUser['role'] | null;
  menuLinks: IParsedUser['menuLinks'];
  whitelabel: IParsedUser['whitelabel'] | null;
  canApproveCampaigns: boolean;
  tabGroups: ITabGroup[];
  ungroupedScreens: ScreenKey[];
  hideDealsInImpressionsByType: boolean;
}

const initialState: IState = {
  userProfile: {
    id: 0,
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  },
  fpdAgreementDate: null,
  screens: [],
  loading: true,
  role: null,
  menuLinks: [],
  whitelabel: null,
  canApproveCampaigns: false,
  tabGroups: [],
  ungroupedScreens: [],
  hideDealsInImpressionsByType: false,
};

export const meSlice = createSlice({
  name: 'me',
  initialState,
  reducers: {
    updateUserProfile: (state, action) => {
      state.userProfile = {
        ...state.userProfile,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getCurrentUser.pending, state => {
        state.loading = true;
      })
      .addCase(getCurrentUser.rejected, state => {
        state.loading = false;
      })
      .addCase(getCurrentUser.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        Sentry.setUser({ id: payload.userProfile.id, email: payload.userProfile.email });
        state.userProfile = payload.userProfile;
        state.screens = payload.screens.filter(screen => ALL_SCREENS.includes(screen.name));
        state.role = payload.role;
        state.menuLinks = payload.menuLinks;
        state.whitelabel = payload.whitelabel;
        state.fpdAgreementDate = payload.fpdAgreementDate;
        state.canApproveCampaigns = payload.canApproveCampaigns;
        state.tabGroups = payload.tabGroups;
        state.ungroupedScreens = payload.ungroupedScreens;
        state.hideDealsInImpressionsByType = payload.hideDealsInImpressionsByType;
        state.loading = false;
      });
  },
});

export const { updateUserProfile } = meSlice.actions;

export default meSlice.reducer;

export const getCurrentUser = createAsyncThunk('meSlice/getCurrentUser', async () => {
  const response = await axios.get(API_ENDPOINT);

  return formatUserResponseData(response?.data);
});

const formatUserResponseData = (data: IUserResponse): IParsedUser | undefined => {
  if (!data) return;
  const {
    id,
    first_name,
    last_name,
    email,
    phone_number,
    screens,
    role_id,
    menu_links,
    whitelabel_id,
    fpd_agreement_signed_at,
    can_approve_campaigns,
    tab_groups,
    hide_deals_in_impressions_by_type,
  } = data;

  return {
    userProfile: {
      id,
      firstName: first_name,
      lastName: last_name,
      email,
      phone: phone_number || '',
    },
    role: role_id,
    screens: [...screens],
    menuLinks: menu_links,
    whitelabel: whitelabel_id,
    fpdAgreementDate: fpd_agreement_signed_at,
    canApproveCampaigns: can_approve_campaigns,
    tabGroups: tab_groups?.groups || [],
    ungroupedScreens: tab_groups?.ungrouped_screens || [],
    hideDealsInImpressionsByType: !!hide_deals_in_impressions_by_type,
  };
};
